import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { PageLayout } from '../components/page-layout';
import { updateActivityName, getWorkout } from 'src/services/workouts.service';
import { Pencil, Check, X, Loader } from 'lucide-react';
import {
  HRZoneStatV2,
  RunSegmentV2,
  StrongifierCompletedWorkoutV2,
  ThresholdHistoryItemV2,
} from 'src/models/strongifier-completed-workout-v2';
import { getActivityVisual } from '../components/home/activity-format';

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

/** min/km float → "6:18 /km" (total-seconds rounding, no :60) */
const fmtPace = (paceMinPerKm: number | null | undefined): string => {
  if (paceMinPerKm == null || !isFinite(paceMinPerKm) || paceMinPerKm <= 0) return '—';
  const totalSeconds = Math.round(paceMinPerKm * 60);
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')} /km`;
};

/** minutes → "52:14" or "1:47:32" */
const fmtDuration = (minutes: number): string => {
  const totalSec = Math.round(minutes * 60);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

/** minutes → "52 min" or "1h 48m" (ledger rows) */
const fmtMinutes = (minutes: number): string => {
  const whole = Math.round(minutes);
  return whole < 60 ? `${whole} min` : `${Math.floor(whole / 60)}h ${String(whole % 60).padStart(2, '0')}m`;
};

/** seconds → "33m" (zone bars) */
const fmtZoneMinutes = (seconds: number): string => `${Math.round(seconds / 60)}m`;

/** "121-138 bpm" → "121–138 bpm" for display */
const fmtRange = (range: string): string => range.replace(/(\d)-(\d)/, '$1–$2');

const localDate = (w: StrongifierCompletedWorkoutV2): Date =>
  new Date(w.local_activity_datetime ?? w.activity_datetime);

const SOURCE_LABELS: Record<string, string> = {
  fit: 'FIT upload',
  manual: 'Manual entry',
  strava_migration: 'Strava migration',
};

const CALC_TYPE_LABELS: Record<string, string> = {
  long_effort: 'Fastest 60-minute segment',
  '45_to_60_minute_effort': 'Fastest 30-minute segment',
  '30_to_45_minute_effort': 'Fastest 20-minute segment',
  sub_thirty_minute_effort: 'Projected from a sub-30-minute effort',
  manual_riegel_projection: 'Projected from hand-logged effort',
  unknown_effort: 'Unknown effort',
};

/** "VirtualRide" → "Ride (virtual)", "WeightTraining" → "Strength", else as-is */
const displayType = (activityType: string): string => {
  const map: Record<string, string> = {
    VirtualRide: 'Ride (virtual)',
    VirtualRun: 'Run (virtual)',
    TrailRun: 'Trail run',
    WeightTraining: 'Strength',
    EBikeRide: 'E-bike ride',
    MountainBikeRide: 'Mountain bike ride',
    GravelRide: 'Gravel ride',
    HighIntensityIntervalTraining: 'HIIT',
  };
  return map[activityType] ?? activityType;
};

const isRun = (w: StrongifierCompletedWorkoutV2) =>
  (w.activity_type || '').toLowerCase().includes('run');
const isSwim = (w: StrongifierCompletedWorkoutV2) =>
  (w.activity_type || '').toLowerCase().includes('swim');

/** Elapsed offset of a segment into the workout ("Starts at 15:04"). The
 *  segment's start_time is a bare wall-clock string whose frame depends on
 *  the source (UTC for FIT-parsed segments), so try both the workout's UTC
 *  and local clocks and accept whichever yields a plausible offset. */
const segmentStartOffset = (w: StrongifierCompletedWorkoutV2, seg: RunSegmentV2): string | null => {
  const match = /^(\d{1,2}):(\d{2}):(\d{2})$/.exec(seg.start_time || '');
  if (!match) return null;
  const segSeconds = +match[1] * 3600 + +match[2] * 60 + +match[3];
  const utcStart = new Date(w.activity_datetime);
  const local = localDate(w);
  const candidates: number[] = [];
  if (!isNaN(utcStart.getTime())) {
    candidates.push(utcStart.getUTCHours() * 3600 + utcStart.getUTCMinutes() * 60 + utcStart.getUTCSeconds());
  }
  if (!isNaN(local.getTime())) {
    candidates.push(local.getHours() * 3600 + local.getMinutes() * 60 + local.getSeconds());
  }
  for (const workoutSeconds of candidates) {
    let elapsed = segSeconds - workoutSeconds;
    if (elapsed < 0) elapsed += 24 * 3600; // workout spanning midnight
    if (elapsed <= w.duration_minutes * 60 + 60) {
      return `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`;
    }
  }
  return null;
};

/** Week number (Monday-based, epoch = week of Jan 1 2024) → "Jul 27 – Aug 2" */
const weekRange = (weekNumber: number): string => {
  const epochMonday = new Date(2024, 0, 1); // Jan 1 2024 is a Monday
  const monday = new Date(epochMonday.getTime() + weekNumber * 7 * 86400000);
  const sunday = new Date(monday.getTime() + 6 * 86400000);
  const fmt = (d: Date, withMonth: boolean) =>
    d.toLocaleDateString('en-US', withMonth ? { month: 'short', day: 'numeric' } : { day: 'numeric' });
  const sameMonth = monday.getMonth() === sunday.getMonth();
  return `${fmt(monday, true)} – ${sameMonth ? fmt(sunday, false) : fmt(sunday, true)}`;
};

// ---------------------------------------------------------------------------
// Section components
// ---------------------------------------------------------------------------

const SectionTitle: React.FC<{ children: React.ReactNode; right?: React.ReactNode }> = ({ children, right }) => (
  <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-3">
    <div className="font-['Chakra_Petch'] text-[15px] font-semibold tracking-[.5px] text-[#e8ecf4]">{children}</div>
    {right}
  </div>
);

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <section className="bg-[#1a2130] border border-[#242e42] rounded-[10px] overflow-hidden">{children}</section>
);

const ZONE_COLORS = ['#6b7688', '#5c8ef0', '#3ec9a7', '#e8c34a', '#e06a3c', '#d4463e'];

const ZonesChart: React.FC<{ zones: HRZoneStatV2[] }> = ({ zones }) => {
  const byNumber = new Map(zones.map((z) => [z.zone_number, z]));
  const max = Math.max(...zones.map((z) => z.time_in_seconds), 1);
  const slots = [0, 1, 2, 3, 4, 5].map((n) => {
    const z = byNumber.get(n);
    if (!z) {
      // Zone 0 absent on Strava-migrated stats: recorded as missing, not zero
      return { n, missing: true as const };
    }
    return { n, missing: false as const, z };
  });
  return (
    <Card>
      <SectionTitle right={<div className="text-[11.5px] text-[#6b7688]">minutes in zone</div>}>
        Heart rate zones
      </SectionTitle>
      <div className="grid grid-cols-6 gap-3 items-end px-5 pb-2 h-[150px]">
        {slots.map((slot) => (
          <div key={slot.n} className="flex flex-col justify-end items-center gap-1.5 h-full">
            {slot.missing ? (
              <>
                <div className="text-[12px] font-semibold text-[#46527a]">—</div>
                <div className="w-full max-w-[64px] rounded-t-[5px] border border-dashed border-[#33405c] h-[12px]" />
              </>
            ) : (
              <>
                <div className={`text-[12px] font-semibold ${slot.z.time_in_seconds === 0 ? 'text-[#46527a]' : 'text-[#dfe5f0]'}`}>
                  {fmtZoneMinutes(slot.z.time_in_seconds)}
                </div>
                <div
                  className="w-full max-w-[64px] rounded-t-[5px]"
                  style={{
                    background: slot.z.time_in_seconds === 0 ? '#232c3e' : ZONE_COLORS[slot.n],
                    height:
                      slot.z.time_in_seconds === 0
                        ? '3px'
                        : `${Math.max(Math.round((slot.z.time_in_seconds / max) * 100), 4)}px`,
                  }}
                />
              </>
            )}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-6 gap-3 px-5 pb-4 border-t border-[#232c3e]">
        {slots.map((slot) => (
          <div key={slot.n} className="text-center pt-2">
            <div className="text-[12px] font-semibold text-[#dfe5f0]">Z{slot.n}</div>
            <div className="text-[10.5px] text-[#6b7688] mt-0.5">
              {slot.missing ? 'not recorded' : fmtRange(slot.z.hr_range)}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const WorkoutDetailPage: React.FC = () => {
  const { workoutId } = useParams<{ workoutId: string }>();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [workout, setWorkout] = useState<StrongifierCompletedWorkoutV2 | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const resp = await getWorkout(token, workoutId || '');
        if (resp.error) setError(resp.error.message);
        else setWorkout(resp.data ?? null);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [getAccessTokenSilently, workoutId]);

  const startNameEdit = () => {
    if (!workout) return;
    setNameValue(workout.workout_name ?? '');
    setNameError(null);
    setEditingName(true);
  };

  const cancelNameEdit = () => {
    setEditingName(false);
    setNameValue('');
    setNameError(null);
  };

  const commitNameEdit = async () => {
    if (!workout) return;
    const trimmed = nameValue.trim();
    if (!trimmed) {
      setNameError('Name cannot be empty.');
      return;
    }
    // Optimistic: show the new name everywhere immediately, spinner until the
    // backend confirms; revert on failure.
    const previousName = workout.workout_name;
    setWorkout({ ...workout, workout_name: trimmed });
    setEditingName(false);
    setSavingName(true);
    setNameError(null);
    try {
      const token = await getAccessTokenSilently();
      const resp = await updateActivityName(token, workout.id, trimmed);
      if (resp.error) {
        setWorkout((prev) => (prev ? { ...prev, workout_name: previousName } : prev));
        setNameError(resp.error.message);
      }
    } catch (e: any) {
      setWorkout((prev) => (prev ? { ...prev, workout_name: previousName } : prev));
      setNameError(e.message);
    } finally {
      setSavingName(false);
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitNameEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelNameEdit();
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="content-layout"><div className="content__body">
          <p className="text-center text-[#9aa5b8]">Loading workout...</p>
        </div></div>
      </PageLayout>
    );
  }
  if (error || !workout) {
    return (
      <PageLayout>
        <div className="content-layout"><div className="content__body">
          <p className="text-center text-red-400">Error: {error || 'Workout not found'}</p>
        </div></div>
      </PageLayout>
    );
  }

  const w = workout;
  const visual = getActivityVisual(w.activity_type);
  const date = localDate(w);
  const dtLabel = date.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  }) + ' · ' + date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  // --- header chips
  const chips: { t: string; c: string; bg: string; bd: string }[] = [
    { t: SOURCE_LABELS[w.source] ?? w.source, c: '#9aa5b8', bg: 'rgba(154,165,184,.08)', bd: '#2e3850' },
  ];
  if (w.analysis_is_long_activity) {
    chips.push({ t: 'Long workout', c: '#8fa3ff', bg: 'rgba(92,108,250,.12)', bd: 'rgba(92,108,250,.45)' });
  }

  // --- stats tiles
  const stats: { l: string; v: string }[] = [{ l: 'Duration', v: fmtDuration(w.duration_minutes) }];
  stats.push({ l: 'Distance', v: w.distance_km > 0 ? `${w.distance_km.toFixed(1)} km` : '—' });
  if (w.distance_km > 0 && isRun(w)) {
    stats.push({ l: 'Avg pace', v: fmtPace(w.duration_minutes / w.distance_km) });
  } else if (w.distance_km > 0 && isSwim(w)) {
    const per100mSeconds = Math.round((w.duration_minutes * 60) / (w.distance_km * 10));
    stats.push({ l: 'Avg pace', v: `${Math.floor(per100mSeconds / 60)}:${String(per100mSeconds % 60).padStart(2, '0')} /100m` });
  }
  stats.push({ l: 'Avg HR', v: w.average_heart_rate != null ? `${Math.round(w.average_heart_rate)} bpm` : '—' });

  // --- zones
  const zoneStats = w.hr_zone_stats?.zones ?? [];
  const hasZones = zoneStats.length > 0;

  // --- segments (runs only)
  const manualReason = 'logged manually — no per-sample data';
  const segmentRows = isRun(w)
    ? [
        { label: 'Fastest 60 min', seg: w.fastest_60_minutes, missReason: w.source === 'manual' ? manualReason : 'workout shorter than 60 minutes' },
        { label: 'Fastest 30 min', seg: w.fastest_30_minutes_after_15_minutes, missReason: w.source === 'manual' ? manualReason : 'workout shorter than 45 minutes' },
        { label: 'Fastest 20 min', seg: w.fastest_20_minutes_after_10_minutes, missReason: w.source === 'manual' ? manualReason : 'workout shorter than 30 minutes' },
      ]
    : [];
  const hasSegs = segmentRows.length > 0;

  // --- threshold analysis (this workout's own measurement)
  const thr = w.analysis_threshold_info;
  const thresholdMoved = w.analysis_new_run_threshold_detected;

  // --- threshold state (carried snapshot)
  const info = w.analysis_running_threshold_information;
  const historyNewestFirst: ThresholdHistoryItemV2[] = info ? [...info.threshold_history].reverse() : [];
  const visibleHistory = showAllHistory ? historyNewestFirst : historyNewestFirst.slice(0, 6);
  const provenanceLabel = (activityId: string | null): string => {
    if (!activityId || !info) return '—';
    const item = info.threshold_history.find((h) => h.activity_id === activityId);
    if (!item) return activityId === w.id ? (w.workout_name ?? 'this workout') : 'a removed workout';
    const name = item.workout_name ?? 'Unnamed workout';
    const itemDate = new Date(item.activity_date);
    return isNaN(itemDate.getTime())
      ? name
      : `${name} · ${itemDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  // --- long-effort ledger
  const ledger = w.analysis_long_leg_dominant_effort_info;
  const hasLedger = w.analysis_is_legs_dominant_endurance_exercise && !!ledger && ledger.value.weeks.length > 0;

  // --- analysis notes (derived)
  const notes: string[] = [];
  if (!isRun(w)) notes.push('Not a run — best-effort segments and threshold detection don’t apply.');
  if (!w.analysis_is_legs_dominant_endurance_exercise) {
    notes.push('Not a leg-dominant endurance activity — excluded from the weekly long-effort ledger.');
  }

  const fitFileName = w.fit_filename ? w.fit_filename.split('/').pop() : null;

  return (
    <PageLayout>
      <div className="content-layout">
        <div className="content__body p-4">
          <div className="max-w-[1180px] mx-auto">
            <div className="mb-[18px]">
              <span
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-[7px] text-[#8fa3ff] text-[13.5px] font-medium cursor-pointer hover:text-[#b3c1ff]"
              >
                ← Back
              </span>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between gap-5 flex-wrap mb-[22px]">
              <div className="flex gap-4 items-center">
                <div
                  className="w-[52px] h-[52px] rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: visual.ringColor }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={visual.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d={visual.iconPath}></path>
                  </svg>
                </div>
                <div>
                  {editingName ? (
                    <div className="flex items-center gap-2 mb-1">
                      <input
                        className="bg-[#10151f] text-[#e8ecf4] font-['Chakra_Petch'] text-[19px] font-semibold px-2 py-1 rounded border border-[#2e3850] focus:outline-none focus:ring-2 focus:ring-[#5661f0]"
                        value={nameValue}
                        onChange={(e) => setNameValue(e.target.value)}
                        onKeyDown={handleNameKeyDown}
                        autoFocus
                        placeholder="Workout name"
                      />
                      <button
                        onClick={commitNameEdit}
                        className="text-green-400 hover:text-green-300"
                        title="Save"
                      >
                        <Check className="w-5 h-5" />
                      </button>
                      <button
                        onClick={cancelNameEdit}
                        className="text-red-400 hover:text-red-300"
                        title="Cancel"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-[1em] mb-1">
                      <h1 className="font-['Chakra_Petch'] text-[23px] font-semibold m-0 tracking-[.4px] text-[#e8ecf4]">
                        {w.workout_name ?? displayType(w.activity_type)}
                      </h1>
                      {savingName ? (
                        <span className="inline-flex items-center gap-1.5 text-[15px] text-[#9aa5b8]" title="Saving name">
                          <Loader className="w-6 h-6 animate-spin" />
                          Saving…
                        </span>
                      ) : (
                        <button
                          onClick={startNameEdit}
                          className="text-[#9aa5b8] hover:text-[#e8ecf4]"
                          title="Edit name"
                        >
                          <Pencil className="w-6 h-6" />
                        </button>
                      )}
                    </div>
                  )}
                  {nameError && <div className="text-xs text-red-400 mb-1">{nameError}</div>}
                  <div className="text-[13px] text-[#9aa5b8]">
                    {dtLabel}{w.local_timezone ? ` · ${w.local_timezone}` : ''} · {displayType(w.activity_type)}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {chips.map((c) => (
                  <span
                    key={c.t}
                    className="text-[11.5px] font-semibold tracking-[.6px] px-[11px] py-[5px] rounded-full"
                    style={{ color: c.c, background: c.bg, border: `1px solid ${c.bd}` }}
                  >
                    {c.t}
                  </span>
                ))}
              </div>
            </div>

            {/* Stats tiles */}
            <div className="grid gap-3 mb-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
              {stats.map((s) => (
                <div key={s.l} className="bg-[#1a2130] border border-[#242e42] rounded-[10px] px-4 py-3.5">
                  <div className="font-['Chakra_Petch'] text-[20px] font-semibold text-[#e8ecf4]">{s.v}</div>
                  <div className="text-[10.5px] tracking-[1.1px] uppercase text-[#6b7688] mt-[3px]">{s.l}</div>
                </div>
              ))}
            </div>

            <div className="grid gap-5 items-start" style={{ gridTemplateColumns: '1.62fr 1fr' }}>
              {/* Left column */}
              <div className="flex flex-col gap-5">
                {hasZones && <ZonesChart zones={zoneStats} />}

                {hasSegs && (
                  <Card>
                    <SectionTitle>Fastest segments</SectionTitle>
                    {segmentRows.map(({ label, seg, missReason }) => (
                      <div key={label} className="grid gap-3.5 items-center px-5 py-3 border-t border-[#1f2839]" style={{ gridTemplateColumns: '1fr auto' }}>
                        <div>
                          <div className="text-[14px] font-semibold text-[#dfe5f0]">{label}</div>
                          <div className="text-[12px] text-[#6b7688] mt-0.5">
                            {seg
                              ? (() => {
                                  const offset = segmentStartOffset(w, seg);
                                  return offset ? `Starts at ${offset}` : `After a ${Math.round(seg.start_after_minutes)}-minute warmup window`;
                                })()
                              : `Not available — ${missReason}`}
                          </div>
                        </div>
                        <div className="flex gap-[22px]">
                          <div className="text-right min-w-[60px]">
                            <div className="text-[14px] font-semibold text-[#dfe5f0]">{seg ? `${seg.distance.toFixed(1)} km` : '—'}</div>
                            <div className="text-[10.5px] tracking-[.8px] uppercase text-[#6b7688] mt-0.5">Distance</div>
                          </div>
                          <div className="text-right min-w-[66px]">
                            <div className="text-[14px] font-semibold text-[#dfe5f0]">{seg ? fmtPace(seg.average_pace) : '—'}</div>
                            <div className="text-[10.5px] tracking-[.8px] uppercase text-[#6b7688] mt-0.5">Pace</div>
                          </div>
                          <div className="text-right min-w-[56px]">
                            <div className="text-[14px] font-semibold text-[#dfe5f0]">
                              {seg && seg.average_heart_rate != null ? `${Math.round(seg.average_heart_rate)} bpm` : '—'}
                            </div>
                            <div className="text-[10.5px] tracking-[.8px] uppercase text-[#6b7688] mt-0.5">Avg HR</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </Card>
                )}

                {thr && (
                  <Card>
                    <SectionTitle
                      right={
                        <span className="text-[11px] font-semibold tracking-[.5px] px-2.5 py-1 rounded-full text-[#9aa5b8] whitespace-nowrap" style={{ background: 'rgba(154,165,184,.08)', border: '1px solid #2e3850' }}>
                          {(thr.calculation_type && CALC_TYPE_LABELS[thr.calculation_type]) || thr.calculation_type || '—'}
                        </span>
                      }
                    >
                      Analyzing for potential threshold pace update
                    </SectionTitle>
                    <div className="px-5 pb-[18px] pt-1">
                      <div className="flex gap-9 mb-3">
                        <div>
                          <div className="font-['Chakra_Petch'] text-[21px] font-semibold text-[#e8ecf4]">{fmtPace(thr.value.threshold_pace)}</div>
                          <div className="text-[10.5px] tracking-[1.1px] uppercase text-[#6b7688] mt-[3px]">Threshold pace</div>
                        </div>
                        <div>
                          <div className="font-['Chakra_Petch'] text-[21px] font-semibold text-[#e8ecf4]">
                            {thr.value.threshold_heart_rate != null ? `${Math.round(thr.value.threshold_heart_rate)} bpm` : '—'}
                          </div>
                          <div className="text-[10.5px] tracking-[1.1px] uppercase text-[#6b7688] mt-[3px]">Threshold HR</div>
                        </div>
                      </div>
                      <div className="text-[13.5px] leading-[1.55] text-[#aab4c8]">{thr.explanation}</div>
                      <div
                        className="inline-flex items-center gap-[7px] mt-3 px-3 py-1.5 rounded-[7px] text-[12.5px] font-bold tracking-[.4px]"
                        style={
                          thresholdMoved
                            ? { color: '#3ec9a7', background: 'rgba(62,201,167,.12)', border: '1px solid rgba(62,201,167,.45)' }
                            : { color: '#8b96aa', background: 'rgba(154,165,184,.06)', border: '1px solid #2e3850' }
                        }
                      >
                        {thresholdMoved ? 'New Threshold Detected!' : 'New threshold NOT detected'}
                      </div>
                    </div>
                  </Card>
                )}

                <Card>
                  <SectionTitle>Threshold state · as of this workout</SectionTitle>
                  <div className="grid grid-cols-2 gap-3 px-5 pb-4">
                    <div className="bg-[#161c29] border border-[#232c3e] rounded-[9px] px-[15px] py-[13px]">
                      <div className="font-['Chakra_Petch'] text-[19px] font-semibold text-[#e8ecf4]">{fmtPace(info?.threshold_pace)}</div>
                      <div className="text-[10.5px] tracking-[1.1px] uppercase text-[#6b7688] mt-[3px]">Threshold pace</div>
                      <div className="text-[11.5px] text-[#8fa3ff] mt-[7px]">Set by {provenanceLabel(info?.threshold_pace_activity_id ?? null)}</div>
                    </div>
                    <div className="bg-[#161c29] border border-[#232c3e] rounded-[9px] px-[15px] py-[13px]">
                      <div className="font-['Chakra_Petch'] text-[19px] font-semibold text-[#e8ecf4]">
                        {info?.threshold_heart_rate != null ? `${Math.round(info.threshold_heart_rate)} bpm` : '—'}
                      </div>
                      <div className="text-[10.5px] tracking-[1.1px] uppercase text-[#6b7688] mt-[3px]">Threshold HR</div>
                      <div className="text-[11.5px] text-[#8fa3ff] mt-[7px]">Set by {provenanceLabel(info?.threshold_heart_rate_activity_id ?? null)}</div>
                    </div>
                  </div>
                  <div className="text-[11px] tracking-[1.2px] uppercase text-[#6b7688] px-5 pb-2">Threshold history</div>
                  {historyNewestFirst.length === 0 && (
                    <div className="px-5 pb-4 text-[13px] text-[#6b7688]">
                      No threshold-setting workouts on record as of this workout.
                    </div>
                  )}
                  {visibleHistory.map((h) => {
                    const hDate = new Date(h.activity_date);
                    return (
                      <div key={h.activity_id + h.activity_date} className="grid gap-4 items-center px-5 py-2.5 border-t border-[#1f2839]" style={{ gridTemplateColumns: '100px 1fr auto auto' }}>
                        <div className="text-[12.5px] text-[#6b7688]">
                          {isNaN(hDate.getTime()) ? '—' : hDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                        <div className="text-[13.5px] font-semibold text-[#dfe5f0]">{h.workout_name ?? 'Unnamed workout'}</div>
                        <div className="text-[13px] font-semibold text-[#dfe5f0] min-w-[64px] text-right">{fmtPace(h.estimated_pace)}</div>
                        <div className="text-[13px] text-[#9aa5b8] min-w-[56px] text-right">
                          {h.estimated_heart_rate != null ? `${Math.round(h.estimated_heart_rate)} bpm` : '—'}
                        </div>
                      </div>
                    );
                  })}
                  {historyNewestFirst.length > 6 && !showAllHistory && (
                    <div
                      onClick={() => setShowAllHistory(true)}
                      className="px-5 py-3 border-t border-[#1f2839] text-[13px] text-[#8fa3ff] cursor-pointer hover:text-[#b3c1ff]"
                    >
                      Show all {historyNewestFirst.length} entries
                    </div>
                  )}
                </Card>
              </div>

              {/* Right column */}
              <div className="flex flex-col gap-5">
                {hasLedger && ledger && (
                  <Card>
                    <SectionTitle>Long-effort ledger</SectionTitle>
                    <div className="px-5 pb-3.5">
                      <div className="text-[12.5px] leading-[1.55] text-[#6b7688] mb-[11px]">
                        For athletes completing 4+ workouts a week, we recommend one longer effort (often called a
                        long run) to specifically train long-effort endurance. Like everything else in endurance
                        sports, this is ideally similar to or slightly greater than previous similar efforts, so we
                        track 'long' efforts here.
                      </div>
                      <div className="mb-[9px]">
                        <span
                          className="text-[11px] font-semibold tracking-[.5px] px-[9px] py-[3px] rounded-full"
                          style={{
                            color: w.analysis_is_long_activity ? '#3ec9a7' : '#9aa5b8',
                            border: '1px solid #2e3850',
                            background: 'rgba(154,165,184,.06)',
                          }}
                        >
                          {w.analysis_is_long_activity ? 'Classified long' : 'Not classified long'}
                        </span>
                      </div>
                      <div className="text-[13px] leading-[1.55] text-[#aab4c8]">{ledger.explanation}</div>
                    </div>
                    {ledger.value.weeks.slice(0, 7).map((wk) => (
                      <div key={wk.week_number}>
                        <div className="flex items-baseline justify-between gap-2.5 px-5 pt-[11px] pb-[7px] border-t border-[#232c3e]">
                          <div className="text-[12px] font-bold tracking-[.5px] text-[#dfe5f0]">
                            Week {wk.week_number} <span className="font-medium text-[#6b7688]">· {weekRange(wk.week_number)}</span>
                          </div>
                          <div className="text-[11px] font-semibold" style={{ color: wk.long_activity ? '#3ec9a7' : '#6b7688' }}>
                            {wk.long_activity ? `Long: ${wk.long_activity.activity_name}` : 'No long activity'}
                          </div>
                        </div>
                        {wk.activities.map((a) => {
                          const aDate = new Date(a.local_datetime);
                          const aIsLong = wk.long_activity?.activity_strongifier_id === a.activity_strongifier_id;
                          return (
                            <div key={a.activity_strongifier_id + a.local_datetime} className="flex items-center justify-between gap-3 px-5 py-[7px]">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-[13px] font-semibold text-[#dfe5f0] truncate">{a.activity_name}</span>
                                {aIsLong && (
                                  <span className="text-[9.5px] font-bold tracking-[1px] px-[7px] py-0.5 rounded-full text-[#3ec9a7] flex-shrink-0" style={{ background: 'rgba(62,201,167,.1)', border: '1px solid rgba(62,201,167,.4)' }}>
                                    LONG
                                  </span>
                                )}
                              </div>
                              <div className="flex gap-3.5 items-baseline flex-shrink-0">
                                <span className="text-[11.5px] text-[#6b7688]">
                                  {isNaN(aDate.getTime())
                                    ? ''
                                    : aDate.toLocaleDateString('en-US', { weekday: 'short' }) + ' · ' + aDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                </span>
                                <span className="text-[12.5px] font-semibold text-[#9aa5b8] min-w-[52px] text-right">{fmtMinutes(a.duration_minutes)}</span>
                              </div>
                            </div>
                          );
                        })}
                        <div className="h-1.5" />
                      </div>
                    ))}
                  </Card>
                )}

                {notes.length > 0 && (
                  <Card>
                    <SectionTitle>Analysis notes</SectionTitle>
                    <div className="px-5 pb-4 flex flex-col gap-[9px]">
                      {notes.map((n) => (
                        <div key={n} className="text-[13px] leading-[1.5] text-[#aab4c8]">{n}</div>
                      ))}
                    </div>
                  </Card>
                )}

                <Card>
                  <SectionTitle>Provenance</SectionTitle>
                  {[
                    { l: 'Workout ID', v: w.id },
                    { l: 'Source', v: SOURCE_LABELS[w.source] ?? w.source },
                    { l: 'FIT file', v: fitFileName ?? '—' },
                  ].map((p) => (
                    <div key={p.l} className="flex items-baseline justify-between gap-3.5 px-5 py-[9px] border-t border-[#1f2839]">
                      <div className="text-[12px] tracking-[.6px] uppercase text-[#6b7688] flex-shrink-0">{p.l}</div>
                      <div className="font-mono text-[12.5px] text-[#dfe5f0] text-right break-all">{p.v}</div>
                    </div>
                  ))}
                  {!w.fit_filename && (
                    <div className="text-[12px] leading-[1.5] text-[#6b7688] px-5 pt-2.5 pb-3.5 border-t border-[#1f2839]">
                      No FIT file — zone stats can’t be recomputed for this workout.
                    </div>
                  )}
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default WorkoutDetailPage;
